import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { deleteTag, getFilteredTags } from '../helpers/tags';

const TagsList = () => {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({
    field: 'name',
    direction: 'asc'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10
  });

  useEffect(() => {
    fetchTags();
  }, [pagination.currentPage, searchTerm, sort]);

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      
      // Create filters object based on your current state
      const filters = {
        search: searchTerm,
        sort: `name:${sort.direction}`
      };

      // Call getFilteredTags with current pagination and sorting
      const { data, pagination: paginationData } = await getFilteredTags(
        filters,
        pagination.currentPage,
        pagination.pageSize
      );

      setTags(data);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(paginationData.total / pagination.pageSize)
      }));
    } catch (err) {
      setError('Erreur lors du chargement des tags');
      console.error('Error fetching tags:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = () => {
    setSort(prev => ({
      field: 'name',
      direction: prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tag ?')) {
      return;
    }

    try {
      const response = await deleteTag(id);
      if(response){
        setTags(prev => prev.filter(est => est.documentId !== id));
        // Afficher message de succès
        const successMessage = document.getElementById('successMessage');
        successMessage.innerText = 'Tag supprimé avec succès';
        successMessage.style.display = 'block';
        setTimeout(() => {
          successMessage.style.display = 'none';
        }, 3000);
      }else{
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error('Error deleting tag:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  if (isLoading && tags.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Gestion des tags</h2>
        <Link to="/tags/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-2"></i>
          Ajouter un tag
        </Link>
      </div>

      <div className="alert alert-success" id="successMessage" style={{ display: 'none' }}></div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-8">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="bg-light">
              <tr>
                <th onClick={handleSort} style={{ cursor: 'pointer' }}>
                  <div className="d-flex align-items-center">
                    Nom
                    <i className={`bi bi-arrow-${sort.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                  </div>
                </th>
                <th width="150">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tags.map(tag => (
                <tr key={tag.id}>
                  <td className="align-middle">
                    <div className="d-flex align-items-center">
                        <div className="fw-semibold">{tag.name}</div>
                    </div>
                  </td>
                  <td className="align-middle">
                    <div className="btn-group">
                      <Link 
                        to={`/tags/${tag.documentId}`}
                        className="btn btn-sm btn-outline-primary"
                        title="Éditer"
                      >
                        <i className="bi bi-pencil"></i>
                      </Link>
                      <button
                        onClick={() => handleDelete(tag.documentId)}
                        className="btn btn-sm btn-outline-danger"
                        title="Supprimer"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
            </li>
            
            {[...Array(pagination.totalPages)].map((_, index) => (
              <li 
                key={index + 1}
                className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}

            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default TagsList;